import { Injectable, inject } from '@angular/core';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { CoreActions } from '@next/core-lib';
import { Store, select } from '@ngrx/store';

import { EventsKey } from 'ol/events';
import { never } from 'ol/events/condition';
import Feature from 'ol/Feature';
import { GeoJSON } from 'ol/format';
import { Select } from 'ol/interaction';
import { VectorImage } from 'ol/layer';
import { tile } from 'ol/loadingstrategy';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Style, Fill, Stroke } from 'ol/style';
import { createXYZ } from 'ol/tilegrid';
import { SiteState } from 'src/app/shared/models';

import { Feature as LocalFeature, InteractionType } from '../../models';
import { InteractionRvoSelected } from '../../store/actions/feature.actions';
import { SetCropFieldsEnabled } from '../../store/actions/map-context.actions';

import { getSelectedRvoIds } from '../../store/selectors/feature.selectors';
import { getRvoEnabled } from '../../store/selectors/map-context.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectRvoService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.cloneRvo];
  private selectEventsKey: EventsKey;
  readonly source = new Vector({
    format: new GeoJSON(),
    strategy: tile(createXYZ({})),
  });

  readonly style = new Style({
    stroke: new Stroke({
      color: MapColors.rvoStroke,
      width: 2,
    }),
    fill: new Fill({
      color: MapColors.invisible,
    }),
  });

  readonly layer = new VectorImage({
    source: this.source,
    style: this.style,
    zIndex: 1,
  });

  readonly select = new Select({
    toggleCondition: never,
    multi: true,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.rvoSelectStroke,
        width: 2,
      }),
      fill: new Fill({
        color: MapColors.rvoFill,
      }),
      zIndex: 5,
    }),
    layers: (layer) => layer === this.layer,
  });

  init(geoService: GeoBrowserService) {
    this.source.setLoader((extent, _resolution, _projection) => {
      const url =
        'https://service.pdok.nl/rvo/brpgewaspercelen/wfs/v1_0?service=WFS&' +
        'version=2.0.0&request=GetFeature&typename=brpgewaspercelen:BrpGewas&' +
        'outputFormat=application/json&srsname=EPSG:3857&' +
        'bbox=' +
        extent.join(',') +
        ',EPSG:3857';
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      const onError = () => {
        this.source.removeLoadedExtent(extent);
      };
      const onLoad = () => {
        if (xhr.status === 200) {
          // Handle errors when service.pdok.nl returns a textual error in the xml
          try {
            const features = new GeoJSON().readFeatures(xhr.responseText);
            for (const feature of features) {
              feature.setId(feature.getProperties()['gid'] as string);
              this.source.addFeature(feature);
            }
          } catch (error) {
            console.log('Error in "new GeoJSON().readFeatures(xhr.responseText)"', error);
            onError();
          }
        } else {
          onError();
        }
      };

      xhr.onerror = onError;
      xhr.onload = onLoad;
      xhr.send();
    });

    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.select);
    this.select.setActive(false);
    this.store.pipe(select(getRvoEnabled)).subscribe((enabled) => {
      if (enabled && geoService.view.getZoom() < 15) {
        geoService.view.setZoom(15);
      }

      this.layer.setVisible(enabled);
    });

    this.store.pipe(select(getSelectedRvoIds)).subscribe((selectedRvoIds) => {
      if (!selectedRvoIds || selectedRvoIds.length === 0) {
        this.select.getFeatures().clear();
      }
    });
    this.layer.setMaxResolution(geoService.map.getView().getResolutionForZoom(14));
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.select);
  }

  start(geoService: GeoBrowserService) {
    this.selectEventsKey = this.select.on('select', (selectEvent) => {
      this.store.dispatch(CoreActions.setLoading({ loading: true }));

      setTimeout(() => {
        const features = selectEvent.selected.filter((feature) => !!feature);
        this.store.dispatch(CoreActions.setLoading({ loading: false }));
        // Bugfix: Only use the first feature to avoid multiple editField dialogs being opened at once
        this.store.dispatch(new InteractionRvoSelected(this.featureToGeoJson(features[0])));
      }, 0);
    });
    this.select.setActive(true);
    this.store.dispatch(new SetCropFieldsEnabled(true));

    if (geoService.view.getZoom() < 15) {
      geoService.view.setZoom(15);
    }
    this.layer.setVisible(true);
  }

  stop(_: GeoBrowserService) {
    unByKey(this.selectEventsKey);
    this.select.setActive(false);
    this.layer.setStyle(this.style);
    this.select.getFeatures().clear();
  }

  private featureToGeoJson(feature: Feature): LocalFeature {
    const writer = new GeoJSON();
    // pass the feature as an array
    return JSON.parse(writer.writeFeature(feature)) as LocalFeature;
  }
}
